/**
 * INTERNAL INTERFACE. This was drafted as a contract to be agreed with
 * PixelChefs, but no vendor ever implements it: their scope was the Snap Lens,
 * so the only implementations are ours — `cameraKitModule.ts` and `stubModule.ts`.
 * It was never agreed with anyone and is not frozen; change it when the app
 * needs it to change, and update both implementations together.
 *
 * It still earns its place as the seam that keeps AR swappable and lets the
 * whole flow run against the stub with no token, which is the only reason the
 * app is testable off a network.
 *
 * Why the shape is what it is:
 *
 * - `setCostumeVisible` exists so *we* own transition timing. We play an MP4
 *   over the top and flip the costume on at full occlusion. The module stays
 *   ignorant of timing.
 * - `getCanvas` returns the live render target. We call `captureStream()` on it
 *   and mux in mic audio ourselves.
 * - The module never touches the microphone. We own all audio.
 */

export interface ARConfig {
  /** Camera selection. Kiosk passes an explicit ID; web leaves it undefined. */
  deviceId?: string;
  /**
   * May the module open a default camera when `deviceId` is absent?
   *
   * Defaults to true, which is the web behaviour and what every existing
   * implementation already does. The **kiosk passes false**: on that panel no
   * camera reports `facingMode: 'user'` (the USB webcam enumerates as
   * EXTERNAL), so the ideal constraint is dropped and Chromium opens the
   * default device — which may be the RK628 HDMI bridge that kernel-panics the
   * board. A module that cannot name the camera must report an error instead of
   * opening one.
   *
   * Additive and optional, so a module that ignores it still compiles. Only
   * cameraKitModule honours it today; stubModule opens whatever a laptop gives
   * it, which is correct for a laptop and never runs on the panel.
   */
  allowDefaultCamera?: boolean;
  /**
   * Optional live camera stream from the permission step. When present the
   * module reuses it instead of calling getUserMedia again — this is the
   * difference between a fast open and a second multi-second camera spin-up.
   */
  videoStream?: MediaStream;
  /**
   * Cap the AR render rate. Omitted or 0 leaves the module's own default, which
   * for Camera Kit is uncapped — see the note at its `createMediaStreamSource`
   * call for why that is a problem on the kiosk GPU and harmless on a phone.
   *
   * Additive and optional, so a module that ignores it still compiles.
   */
  fpsLimit?: number;
  onReady: () => void;
  /** Fired when the guest opens their mouth. */
  onTrigger: () => void;
  onError: (err: Error) => void;
}

export interface ARModule {
  init(container: HTMLElement, config: ARConfig): Promise<void>;
  start(): void;
  stop(): void;
  setCostumeVisible(visible: boolean): void;
  getCanvas(): HTMLCanvasElement;
  destroy(): void;
  /**
   * Optional: begin whatever can begin without a container or callbacks — for
   * the remote module, the WebRTC negotiation and the first frames. The flow
   * calls this on the attract tap so the instruction clip covers the connect,
   * and `init()` then finds the picture already there. See prepareARModule.
   */
  preconnect?(): void;
  /**
   * Optional: the flow will now accept a trigger (the grace period after the
   * camera went live is over — see timing.triggerGraceMs). The remote module
   * forwards this so the render machine can hand the guest an untransformed
   * lens if their arrival already set it off.
   */
  arm?(): void;
  /**
   * Optional: the transition is starting now. A module that renders on another
   * machine plays the fog there, into the stream, so the panel draws none of it
   * — see remoteModule and docs/HANDOFF.md. Modules rendering locally ignore
   * this; the flow's own overlay covers them.
   */
  startTransition?(): void;
  /**
   * Optional: decoder statistics for the capture-health sampler
   * (src/lib/captureHealth.ts). Only meaningful for a streamed source.
   */
  getDebugStats?(): Promise<import('../lib/captureHealth').RemoteVideoStats | null>;
}

/**
 * Render target dimensions. 9:16 portrait, 720p — also the capture size.
 *
 * 720p is deliberate, not a placeholder. A full-viewport canvas on the kiosk
 * would be 2160x3840 device pixels — far too much fill for the unit's Mali-G52
 * 2EE while it is also running tracking and an encode. We render at 720x1280 and
 * let the compositor upscale.
 *
 * The panel's exact resolution is unconfirmed (see docs/KIOSK_HARDWARE.md), but
 * this number does not depend on it: the GPU budget on a signage-grade Mali-G52
 * 2EE settles it either way.
 */
export const AR_CANVAS_WIDTH = 720;
export const AR_CANVAS_HEIGHT = 1280;
